import React, { useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// using the uploaded file path for the logo (from this session)
const logoUrl = "./avantica_pumps_logo-1.png";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/quotations";

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      setError("Please fill in both fields");
      return;
    }

    const { success, message } = await login(username, password);
    if (success) {
      navigate(from, { replace: true });
    } else {
      setError(message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 flex items-center justify-center p-6 overflow-hidden">
      
      {/* Floating Orbs */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute w-96 h-96 -top-40 -left-40 bg-purple-600 rounded-full blur-3xl opacity-40 animate-pulse"></div>
        <div className="absolute w-80 h-80 -bottom-40 -right-40 bg-cyan-600 rounded-full blur-3xl opacity-40 animate-pulse" style={{ animationDelay: "-3s" }}></div>
        <div className="absolute w-72 h-72 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-pink-600 rounded-full blur-3xl opacity-30 animate-pulse" style={{ animationDelay: "-6s" }}></div>
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white/8 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-2xl p-10">
          
          {/* Logo: improved visibility */}
          <div className="flex justify-center mb-8">
            <div className="relative flex items-center justify-center">
              {/* Outer halo gradient (soft) */}
              <div
                className="absolute rounded-2xl w-36 h-36 -z-20"
                style={{
                  background: "radial-gradient(circle at 30% 20%, rgba(255,128,230,0.12), rgba(99,102,241,0.08) 30%, transparent 55%)",
                  filter: "blur(18px)",
                }}
              />
              {/* strong backing circle for contrast + ring */}
              <div className="relative rounded-2xl w-36 h-36 flex items-center justify-center"
                   style={{
                     background: "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.02))",
                     boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 30px rgba(0,0,0,0.45)",
                     borderRadius: "18px"
                   }}>
                <div className="rounded-xl w-28 h-28 flex items-center justify-center"
                     style={{
                       background: "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                       border: "3px solid rgba(255,255,255,0.18)",
                       boxShadow: "0 8px 30px rgba(99,102,241,0.12)"
                     }}>
                  {/* image with increased contrast/saturation */}
                  <img
                    src={logoUrl}
                    alt="Avantica Pumps"
                    className="w-20 h-20 object-contain rounded-lg"
                    style={{ filter: "contrast(1.15) saturate(1.15)", transform: "translateZ(0)" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-4xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-purple-300 via-pink-300 to-cyan-300 mb-10 tracking-tight">
            Welcome Back
          </h2>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-6 py-5 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-purple-300 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-500/30 transition-all text-lg backdrop-blur-xl"
              />
            </div>

            <div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-6 py-5 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-purple-300 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-500/30 transition-all text-lg backdrop-blur-xl"
              />
            </div>

            {error && (
              <div className="text-red-400 text-center font-medium bg-red-500/10 py-3 rounded-xl border border-red-500/30">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-xl py-5 rounded-2xl shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-purple-500/50 flex items-center justify-center gap-3"
            >
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c1.104 0 2-.896 2-2s-.896-2-2-2-2 .896-2 2 .896 2 2 2zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              Login to Portal
            </button>
          </form>

          {/* Footer */}
          <div className="text-center mt-10 text-purple-300 text-sm">
            <p className="font-bold text-xl">Avantica Pumps Pvt Ltd</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
